import { BarChart3Icon, FilterIcon, MailIcon, PlusIcon, SearchIcon, SirenIcon, UsersIcon, BoxIcon } from "lucide-react";
import { HUES, avatarHue, Hue } from "../alt/hues";
type Skill = {
  name: string;
  description?: string;
  icon: BoxIcon;
  group: string;
  by: string;
  initials: string;
  hue: Hue;
  used: number;
  members: number;
};
type SkillShelfProps = {
  /** Kept for the existing export registrations. Both variants use the same
   * list hierarchy; the narrow one simply hides lower-priority details. */
  columns?: 1 | 4;
  spread?: 1 | 2;
};
const SKILLS: Skill[] = [{
  name: 'Inbound lead qualification',
  description: 'Scores a new lead against our ICP, checks the account in Salesforce, and drafts the first reply.',
  icon: FilterIcon,
  group: 'Marketing',
  by: 'Priya Nair',
  initials: 'PN',
  hue: HUES.clay,
  used: 142,
  members: 24
}, {
  name: 'Renewal email drafting',
  icon: MailIcon,
  group: 'Revenue',
  by: 'Marcus Lee',
  initials: 'ML',
  hue: HUES.teal,
  used: 211,
  members: 31
}, {
  name: 'Weekly reporting format',
  icon: BarChart3Icon,
  group: 'Leadership',
  by: 'Sarah Chen',
  initials: 'SC',
  hue: HUES.indigo,
  used: 96,
  members: 8
}, {
  name: 'P1 incident escalation',
  icon: SirenIcon,
  group: 'Customer Success',
  by: 'Tom Alvarez',
  initials: 'TA',
  hue: HUES.plum,
  used: 38,
  members: 17
}];
export function SkillShelf({
  columns,
  spread
}: SkillShelfProps = {}) {
  const compact = columns === 1 || spread === 1;
  const [featured, ...rest] = SKILLS;
  return <section aria-label="Skills library" className="w-full overflow-hidden rounded-2xl border border-[#E9E1D6] bg-white p-6 text-[#2B211A] shadow-[0_18px_50px_-32px_rgba(43,33,26,0.3)]" data-id="element-3432" data-mp="1bjbrml95u1k4">
      <header className="flex items-end justify-between gap-5 border-b border-[#EFE7DA] pb-4" data-id="element-3433" data-mp="1l9xy9pywfhqo">
        <div data-id="element-3434" data-mp="1z961qv23hc0o">
          <h2 className="text-[22px] font-semibold tracking-[-0.02em]" data-id="element-3435" data-mp="1zb0ngbl6x85t">Skills</h2>
          <p className="mt-1 text-[12.5px] text-[#8A7B6D]" data-id="element-3436" data-mp="1ube89wlsxhud">Reusable methods your team and agents can run.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2" data-id="element-3437" data-mp="ryrhvtlk5wvj">
          {!compact && <span className="flex h-8 items-center gap-2 rounded-lg border border-[#E9E1D6] px-3 text-[11.5px] text-[#9C8D80]" data-id="element-3438" data-mp="vyjvikpt6t7r">
              <SearchIcon size={12} strokeWidth={2} data-id="element-3439" data-mp="10xcznmj7h7v9" />
              Search
            </span>}
          <span className="flex h-8 items-center gap-1.5 rounded-lg bg-[#2B211A] px-3 text-[11.5px] font-semibold text-white" data-id="element-3440" data-mp="15pwjh18hkz8i">
            <PlusIcon size={12} strokeWidth={2.4} data-id="element-3441" data-mp="1anoalt1b4hbn" />
            New skill
          </span>
        </div>
      </header>

      <FeaturedSkill skill={featured} compact={compact} data-id="element-3442" data-mp="1fovn25eea4on" />

      <div className="mt-3" data-id="element-3443" data-mp="1jpxmam8bagqm">
        {rest.map(skill => <SkillRow key={skill.name} skill={skill} compact={compact} data-id="element-3444" data-mp="1onqpd6ebiumo" />)}
      </div>
    </section>;
}
function FeaturedSkill({
  skill,
  compact
}: {
  skill: Skill;
  compact: boolean;
}) {
  return <article className="mt-5 rounded-xl bg-[#FAF8F4] px-5 py-4" data-id="element-3445" data-mp="3qf3v1xo416js">
      <div className="flex items-start justify-between gap-5" data-id="element-3446" data-mp="3qflxjc1d8h05">
        <div className="min-w-0" data-id="element-3447" data-mp="3ivula3boz5u7">
          <div className="flex flex-wrap items-center gap-2" data-id="element-3448" data-mp="3ix5rsawiurp0">
            <h3 className="text-[17px] font-semibold tracking-[-0.015em]" data-id="element-3449" data-mp="3e4kwt61vq8jw">{skill.name}</h3>
            <span className="rounded-md bg-[#FDF0E6] px-2 py-1 text-[9.5px] font-semibold text-[#C2410C]" data-id="element-3450" data-mp="3958dgva7vjm2">Most used</span>
          </div>
          <p className="mt-2 max-w-[58ch] text-[12.5px] leading-relaxed text-[#6B5D52]" data-id="element-3451" data-mp="3489jccqjzcnv">{skill.description}</p>
        </div>
        <div className="shrink-0 text-right" data-id="element-3452" data-mp="5myhtz739j5x">
          <p className="text-[24px] font-semibold leading-none tabular-nums" data-id="element-3453" data-mp="ponq68r8jbr">{skill.used}</p>
          <p className="mt-1 text-[9.5px] font-medium text-[#9C8D80]" data-id="element-3454" data-mp="3rvhomunzelpw">runs</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11.5px]" data-id="element-3455" data-mp="3nvfjgbj1fa5v">
        <span className="flex items-center gap-2 text-[#8A7B6D]" data-id="element-3456" data-mp="2ghlxsonpsoiu">
          <span className="flex size-6 items-center justify-center rounded-full text-[8px] font-bold text-white" style={{
          backgroundColor: avatarHue(skill.initials)
        }} data-id="element-3457" data-mp="2gerdcx4pb3pf">{skill.initials}</span>
          {skill.by}
        </span>
        <span className="flex items-center gap-1.5 font-medium" style={{
        color: skill.hue.ink
      }} data-id="element-3458" data-mp="8xspk31oj2vk">
          <span className="size-1.5 rounded-full" style={{
          backgroundColor: skill.hue.solid
        }} data-id="element-3459" data-mp="3qfbzft9gdnd1" />
          {skill.group}
        </span>
        {!compact && <span className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[#6B5D52]" data-id="element-3460" data-mp="mxk2s1kdgf8i">
            <UsersIcon size={12} data-id="element-3461" data-mp="rwwzde11p0oa" />
            {skill.members} can run it
          </span>}
      </div>
    </article>;
}
function SkillRow({
  skill,
  compact
}: {
  skill: Skill;
  compact: boolean;
}) {
  const Icon = skill.icon;
  return <article className="flex items-center gap-3 border-b border-[#EFE7DA] px-1 py-3.5 last:border-0" data-id="element-3462" data-mp="hww9tacl1obn">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-xl" style={{
      backgroundColor: skill.hue.tint,
      color: skill.hue.ink
    }} data-id="element-3463" data-mp="2yl9wpb6xwcsz">
        <Icon size={14} strokeWidth={2} data-id="element-3464" data-mp="2v44cf9e0zh7o" />
      </span>
      <div className="min-w-0 flex-1" data-id="element-3465" data-mp="2v708sta85xu0">
        <h3 className="truncate text-[14px] font-semibold" data-id="element-3466" data-mp="2q6c9zlfr8cp8">{skill.name}</h3>
        <p className="mt-0.5 truncate text-[11.5px] text-[#9C8D80]" data-id="element-3467" data-mp="3533a5wx95sfw">
          {skill.by}
          <span className="px-1.5" data-id="element-3468" data-mp="350quxrivpw6j">·</span>
          <span className="font-medium" style={{
          color: skill.hue.ink
        }} data-id="element-3469" data-mp="303qm58unafbq">{skill.group}</span>
          {!compact && <>
              <span className="px-1.5" data-id="element-3470" data-mp="2ccbincr4tryw">·</span>
              {skill.members} can run it
            </>}
        </p>
      </div>
      <p className="shrink-0 text-[12px] font-semibold tabular-nums text-[#6B5D52]" data-id="element-3471" data-mp="2m48wysdft01u">{skill.used} runs</p>
    </article>;
}